import type { FindProductionSitesForSupplierResponseDto } from "~/gen/api"

import type { ProductionSiteFormValues } from "../schemas/productionSiteForm.schema"

type EditableRegistrationId = {
  readonly id?: string | undefined
  readonly registrationId: string
  readonly type: string
}

const areStringArraysEqual = (
  left: ReadonlyArray<string>,
  right: ReadonlyArray<string>
): boolean => {
  if (left.length !== right.length) {
    return false
  }

  return left.every((value, index) => value === right[index])
}

const areRegistrationIdsEqual = (
  left: ReadonlyArray<EditableRegistrationId>,
  right: ReadonlyArray<EditableRegistrationId>
): boolean => {
  if (left.length !== right.length) {
    return false
  }

  return left.every((leftRegistrationId, index) => {
    const rightRegistrationId = right[index]
    if (rightRegistrationId === undefined) {
      return false
    }

    return (
      leftRegistrationId.id === rightRegistrationId.id &&
      leftRegistrationId.registrationId === rightRegistrationId.registrationId &&
      leftRegistrationId.type === rightRegistrationId.type
    )
  })
}

const mapProductionSiteRegistrationIds = (
  productionSite: FindProductionSitesForSupplierResponseDto
): ReadonlyArray<EditableRegistrationId> => {
  return (
    productionSite.registrationIds?.map((registrationId) => ({
      id: registrationId.id,
      registrationId: registrationId.registrationId,
      type: registrationId.type,
    })) ?? []
  )
}

export function hasNonParentFieldChanges(
  values: ProductionSiteFormValues,
  productionSite: FindProductionSitesForSupplierResponseDto
): boolean {
  const productionSitePerformedActivityIds =
    productionSite.performedActivities?.map((activity) => activity.id) ?? []
  const productionSiteRegistrationIds =
    mapProductionSiteRegistrationIds(productionSite)
  const formRegistrationIds = values.registrationIds ?? []

  return (
    values.name !== productionSite.name ||
    values.internalCode !== (productionSite.internalCode ?? "") ||
    values.street !== (productionSite.street ?? "") ||
    values.city !== (productionSite.city ?? "") ||
    values.postalCode !== (productionSite.postalCode ?? "") ||
    values.longitude !== (productionSite.longitude ?? undefined) ||
    values.latitude !== (productionSite.latitude ?? undefined) ||
    values.countryIso3166 !== productionSite.country ||
    !areStringArraysEqual(
      values.performedActivities,
      productionSitePerformedActivityIds
    ) ||
    !areRegistrationIdsEqual(formRegistrationIds, productionSiteRegistrationIds)
  )
}
